import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Users,
  Plus,
  MapPin,
  Calendar,
  Tag,
  MessageCircle
} from 'lucide-react';
import { useGroupStore } from '../store/groupStore';
import { useEventStore } from '../store/eventStore';
import { groupService } from '../services/groupService';
import { eventService } from '../services/eventService';
import { premiumService } from '../services/premiumService';
import { LoadingSpinner, Button, ProfileAvatar, UserProfilePopover, CapacityNotice, FeaturedImageHeader } from '../components/ui';
import { PageHeader, ContentCard } from '../components/layout';
import { toast } from '../components/ui/Toaster';
import { useNotice } from '../contexts/NoticeContext';
import UpgradeModal from '../components/modals/UpgradeModal';
import type { Group, GroupFilters } from '../types';

const GroupsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const eventIdParam = searchParams.get('eventId');

  const {
    groups,
    pagination,
    isLoading,
    error,
    currentPage,
    setGroups,
    setLoading,
    setError,
    setCurrentPage,
    clearError,
  } = useGroupStore();

  const {
    setActiveEvents,
  } = useEventStore();

  const { addNotice: _addNotice } = useNotice();

  // Context detection
  const isEventDiscovery = !!eventIdParam;
  const isMyGroups = !eventIdParam;

  const [filters] = useState<GroupFilters>({
    availableOnly: true,
    tags: [],
    eventId: eventIdParam ? parseInt(eventIdParam) : undefined,
  });
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [canCreateGroup, setCanCreateGroup] = useState(true);
  const [showLimitNotice, setShowLimitNotice] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Load initial data
  useEffect(() => {
    if (isEventDiscovery) {
      loadEvents();
    }
    loadGroups();
    checkGroupLimit();
  }, []);

  const checkGroupLimit = async () => {
    try {
      const result = await premiumService.canCreateGroup();

      // Handle undefined result (API failure)
      if (!result) {
        setCanCreateGroup(true);
        return;
      }

      setCanCreateGroup(result.canJoin);

      if (!result.canJoin && result.requiresUpgrade) {
        setShowLimitNotice(true);
        setLimitMessage(result.reason || 'Je kunt op dit moment maar in 3 groepen tegelijkertijd. Wil je meer? Steun ons dan met een biertje (€4,95) en dan kun je voor altijd in 6 groepen tegelijkertijd.');
      } else {
        setShowLimitNotice(false);
      }
    } catch (error) {
      console.error('❌ [GroupsPage] Failed to check group limit:', error);
      // On error, default to allowing group creation to avoid blocking users
      setCanCreateGroup(true);
    }
  };

  const handleUpgradeClick = () => {
    setShowUpgradeModal(true);
  };

  const handleUpgradeComplete = () => {
    setShowUpgradeModal(false);
    // Refresh the limit check after upgrade
    checkGroupLimit();
  };

  // Load groups when context, filters, or page changes
  useEffect(() => {
    loadGroups();
  }, [isEventDiscovery, filters, currentPage]);

  // Load selected event details when eventId changes
  useEffect(() => {
    if (filters.eventId) {
      loadSelectedEvent();
    } else {
      setSelectedEvent(null);
    }
  }, [filters.eventId]);

  const loadEvents = async () => {
    try {
      const response = await eventService.getActiveEvents({ limit: 50 });
      setActiveEvents(response);
    } catch (error) {
      console.error('Failed to load events:', error);
    }
  };

  const loadSelectedEvent = async () => {
    if (!filters.eventId) return;

    try {
      const event = await eventService.getEvent(filters.eventId);
      setSelectedEvent(event);
    } catch (error) {
      console.error('Failed to load selected event:', error);
    }
  };

  const loadGroups = async () => {
    try {
      setLoading(true);
      clearError();

      let response;

      if (isEventDiscovery) {
        // Event discovery mode: Show all groups for the event (joinable + joined)
        response = await groupService.getGroups(
          filters,
          { page: currentPage, limit: 12 }
        );
      } else {
        // My groups mode: Show only user's joined groups
        response = await groupService.getMyGroups(
          { page: currentPage, limit: 12 }
        );
      }

      setGroups(response);
    } catch (error: any) {
      console.error('Failed to load groups:', error);
      setError(error.message || 'Laden van groepen mislukt');
      toast.error('Fout', 'Laden van groepen mislukt');
    }
  };

  const handleJoinGroup = async (groupId: number) => {
    try {
      await groupService.joinGroup(groupId);
      toast.success('Succes', 'Aanvraag om lid te worden succesvol verzonden!');
      loadGroups(); // Refresh to update membership status
    } catch (error: any) {
      console.error('Failed to join group:', error);
      toast.error('Fout', error.message || 'Aansluiten bij groep mislukt');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const renderGroupCard = (group: Group) => (
    <div key={group.id} className="sf-card flex h-full flex-col overflow-hidden">
      <Link
        to={`/groups/${group.id}`}
        className="block"
        aria-label={`Bekijk ${group.name}`}
      >
        <FeaturedImageHeader
          imageUrl={group.featuredImage || null}
          title={group.name}
        />
      </Link>
      <div className="flex flex-1 flex-col p-6">
        {/* Content area that grows to fill available space */}
        <div className="flex-1">
          {/* Group name + unread chat badge */}
          <h3 className="mb-2 flex items-center gap-2 text-lg font-bold text-fg-bright">
            <Link
              to={`/groups/${group.id}`}
              className="truncate"
            >
              {group.name}
            </Link>
            {(group.unreadCount ?? 0) > 0 && (
              <span
                className="inline-flex h-5 min-w-[20px] flex-shrink-0 items-center justify-center rounded-full bg-neon-pink px-1.5 text-[11px] font-bold text-white"
                title={`${group.unreadCount} ongelezen ${group.unreadCount === 1 ? 'bericht' : 'berichten'}`}
              >
                {group.unreadCount! > 99 ? '99+' : group.unreadCount}
              </span>
            )}
          </h3>

          {/* Group description excerpt */}
          <p className="mb-3 text-sm text-fg-body line-clamp-2">
            {group.description}
          </p>

          {/* Event name on single row */}
          {group.event && (
            <div className="mb-3 flex items-center text-sm text-fg-muted">
              <MapPin className="h-4 w-4 mr-1.5 text-neon-pink" />
              <span className="font-medium text-fg-soft">{group.event.name}</span>
            </div>
          )}

          {/* Member count and creator in two columns */}
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div className="flex items-center text-sm text-fg-muted">
              <Users className="h-4 w-4 mr-1.5 text-neon-cyan" />
              <span>
                {group.currentMembers}/{group.maxMembers} leden
              </span>
            </div>
            {group.owner && (
              <UserProfilePopover
                userId={group.owner.id}
                placement="auto"
                trigger={
                  <div className="flex cursor-pointer items-center justify-end text-sm text-fg-muted transition-colors hover:text-fg-soft">
                    <ProfileAvatar
                      user={group.owner}
                      size="xs"
                      className="mr-1.5"
                    />
                    <span>{group.owner.username}</span>
                  </div>
                }
              />
            )}
          </div>

          {/* Tags */}
          {group.tags && group.tags.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-1.5">
              {group.tags.slice(0, 3).map((tag, index) => (
                <span
                  key={index}
                  className="sf-badge-cyan"
                >
                  <Tag className="h-3 w-3 mr-1" />
                  {tag}
                </span>
              ))}
              {group.tags.length > 3 && (
                <span className="text-xs text-fg-muted">
                  +{group.tags.length - 3} meer
                </span>
              )}
            </div>
          )}
        </div>

        {/* Actions - will stick to bottom */}
        <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-3">
          <Link to={`/groups/${group.id}`} className="w-full sm:flex-1">
            <Button
              variant="secondary"
              size="md"
              className="w-full min-h-[44px] sm:min-h-[36px] text-base sm:text-sm"
            >
              {isMyGroups ? 'Open groep' : 'Bekijk details'}
            </Button>
          </Link>

          {!isMyGroups && (
            <>
              {group.membershipStatus === 'NOT_MEMBER' ? (
                group.currentMembers < group.maxMembers ? (
                  <Button
                    onClick={() => handleJoinGroup(group.id)}
                    size="md"
                    className="w-full sm:flex-1 min-h-[44px] sm:min-h-[36px] text-base sm:text-sm"
                  >
                    Groep joinen
                  </Button>
                ) : (
                  <Button
                    disabled
                    variant="secondary"
                    size="md"
                    className="w-full sm:flex-1 min-h-[44px] sm:min-h-[36px] text-base sm:text-sm cursor-not-allowed"
                  >
                    Groep vol
                  </Button>
                )
              ) : group.membershipStatus === 'PENDING' ? (
                <Button
                  disabled
                  variant="secondary"
                  size="md"
                  className="w-full sm:flex-1 min-h-[44px] sm:min-h-[36px] text-base sm:text-sm cursor-not-allowed"
                >
                  Verzoek in behandeling
                </Button>
              ) : group.membershipStatus === 'ACCEPTED' ? (
                <Link to={`/groups/${group.id}/chat`} className="w-full sm:flex-1">
                  <Button
                    size="md"
                    className="w-full min-h-[44px] sm:min-h-[36px] text-base sm:text-sm"
                  >
                    <MessageCircle className="h-4 w-4 mr-1" />
                    Groep chat
                  </Button>
                </Link>
              ) : null}
            </>
          )}

          {isMyGroups && (
            <Link to={`/groups/${group.id}/chat`} className="w-full sm:flex-1">
              <Button
                size="md"
                className="w-full min-h-[44px] sm:min-h-[36px] text-base sm:text-sm"
              >
                <MessageCircle className="h-4 w-4 mr-1" />
                Groep chat
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      {isEventDiscovery && selectedEvent ? (
        <PageHeader
          title={`Groepen voor ${selectedEvent.title}`}
          subtitle={
            <div className="flex flex-col gap-1 text-sm text-fg-muted sm:flex-row sm:items-center sm:gap-4">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-neon-cyan" />
                <span>{new Date(selectedEvent.startDate).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-neon-pink" />
                <span>{selectedEvent.location}</span>
              </div>
            </div>
          }
          breadcrumb={
            <>
              <Link to="/events" className="text-sm text-fg-muted transition-colors hover:text-fg-soft">
                ← Terug naar evenementen
              </Link>
              <div className="sf-eyebrow mt-4">// groepen</div>
            </>
          }
          actions={
            canCreateGroup ? (
              <Link to={`/groups/create${eventIdParam ? `?eventId=${eventIdParam}` : ''}`} className="w-full sm:w-auto">
                <Button size="md" className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Groep aanmaken
                </Button>
              </Link>
            ) : (
              <Button
                size="md"
                disabled
                variant="secondary"
                className="cursor-not-allowed w-full"
                title="Je hebt je groep limiet bereikt. Upgrade voor meer groepen."
              >
                <Plus className="h-4 w-4 mr-2" />
                Limiet bereikt
              </Button>
            )
          }
        />
      ) : (
        <PageHeader
          title={isEventDiscovery ? 'Alle groepen' : 'Mijn groepen'}
          subtitle={isEventDiscovery
            ? 'Ontdek en join groepen voor dit evenement'
            : 'Dit zijn de groepen waar je lid van bent. Geen leuke groep beschikbaar? Maak er dan zelf één aan!'
          }
          breadcrumb={<div className="sf-eyebrow">// groepen</div>}
          actions={
            canCreateGroup ? (
              <Link to={`/groups/create${eventIdParam ? `?eventId=${eventIdParam}` : ''}`} className="w-full sm:w-auto">
                <Button size="md" className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Groep aanmaken
                </Button>
              </Link>
            ) : (
              <Button
                size="md"
                disabled
                variant="secondary"
                className="cursor-not-allowed w-full"
                title="Je hebt je groep limiet bereikt. Upgrade voor meer groepen."
              >
                <Plus className="h-4 w-4 mr-2" />
                Limiet bereikt
              </Button>
            )
          }
        />
      )}

      {/* Limit Notice */}
      {showLimitNotice && (
        <CapacityNotice
          title="Groep limiet bereikt"
          message={limitMessage}
          onUpgradeClick={handleUpgradeClick}
        />
      )}

      {/* Main Content */}
      <ContentCard>
        {/* Group count */}
        <div className="mb-6">
          <span className="sf-label-mono">
            {pagination && `${pagination.total} groepen gevonden`}
          </span>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" text="Groepen laden..." />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="mb-6 rounded-field border border-neon-pink/40 bg-neon-pink/10 p-4">
            <div className="text-sm text-neon-pink-soft">{error}</div>
          </div>
        )}

        {/* Groups Grid/List */}
        {!isLoading && !error && (
          <>
            {!groups || groups.length === 0 ? (
              <div className="text-center py-12">
                <Users className="mx-auto h-12 w-12 text-fg-faint mb-4" />
                <h3 className="text-lg font-bold text-fg-bright mb-2">
                  {isEventDiscovery ? 'Geen groepen gevonden' : 'Nog geen groepen gejoint'}
                </h3>
                <p className="text-fg-muted mb-4">
                  {isEventDiscovery
                    ? 'Er zijn nog geen groepen aangemaakt voor dit evenement. Wees de eerste die er een aanmaakt!'
                    : 'Je hebt je nog niet aangesloten bij groepen. Bekijk evenementen om groepen te vinden.'
                  }
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  {!isEventDiscovery && (
                    <Link to="/events">
                      <Button variant="secondary" size="md">
                        Bekijk evenementen
                      </Button>
                    </Link>
                  )}
                  {canCreateGroup ? (
                    <Link to={`/groups/create${eventIdParam ? `?eventId=${eventIdParam}` : ''}`}>
                      <Button size="md">
                        <Plus className="h-4 w-4 mr-2" />
                        Groep aanmaken
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      size="md"
                      disabled
                      variant="secondary"
                      className="cursor-not-allowed"
                      title="Je hebt je groep limiet bereikt. Upgrade voor meer groepen."
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Limiet bereikt
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {groups?.map(group => renderGroupCard(group))}
              </div>
            )}

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-white/[.08] pt-4 mt-6">
                <div className="flex flex-1 justify-between sm:hidden">
                  <Button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!pagination.hasPreviousPage}
                    variant="secondary"
                    size="md"
                  >
                    Vorige
                  </Button>
                  <Button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                    variant="secondary"
                    size="md"
                  >
                    Volgende
                  </Button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-fg-muted">
                      Toont{' '}
                      <span className="font-medium text-fg-soft">
                        {(currentPage - 1) * pagination.limit + 1}
                      </span>{' '}
                      tot{' '}
                      <span className="font-medium text-fg-soft">
                        {Math.min(currentPage * pagination.limit, pagination.total)}
                      </span>{' '}
                      van <span className="font-medium text-fg-soft">{pagination.total}</span> resultaten
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex gap-2">
                      <Button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={!pagination.hasPreviousPage}
                        variant="secondary"
                        size="sm"
                      >
                        Vorige
                      </Button>
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <Button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            variant={page === currentPage ? 'primary' : 'secondary'}
                            size="sm"
                          >
                            {page}
                          </Button>
                        );
                      })}
                      <Button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={!pagination.hasNextPage}
                        variant="secondary"
                        size="sm"
                      >
                        Volgende
                      </Button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </ContentCard>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          upgradeType="EXTRA_GROUP_SLOTS"
          onUpgradeComplete={handleUpgradeComplete}
        />
      )}
    </div>
  );
};

export default GroupsPage;